import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Body, Button, H2 } from "@datapay-by-datacom/design-system";
import { FileDropzone, ImportProgress, WorkflowActionBar } from "../../shared";
import { UnsavedProgressConfirmModal } from "../../onboarding/components/UnsavedProgressConfirmModal";
import { useUnsavedProgressNavigationGuard } from "../../onboarding/hooks/useUnsavedProgressNavigationGuard";
import { COMPONENT_MATCHING_IMPORT_BASE_PATH } from "../config";
import { MatchingBulkActionBar } from "../components/MatchingBulkActionBar";
import type { MatchingBulkScope } from "../components/MatchingBulkActionBar";
import { MatchingBulkActionConfirmModal } from "../components/MatchingBulkActionConfirmModal";
import { MatchingImportCancelConfirmModal } from "../components/MatchingImportCancelConfirmModal";
import { MatchingImportConfirmModal } from "../components/MatchingImportConfirmModal";
import type { MatchingConfirmMode } from "../components/MatchingImportConfirmModal";
import { MatchingImportReport } from "../components/MatchingImportReport";
import { MatchingRowEditModal } from "../components/MatchingRowEditModal";
import { MatchingWizardCtx } from "../components/MatchingWizardContext";
import {
  buildMatchingIssuesCsv,
  buildMatchingParseFailureCsv,
  downloadMatchingIssuesCsv,
  matchingIssuesFilename,
} from "../utils/matchingImportIssuesCsv";
import {
  buildMatchingParseFailureReport,
  matchingParseFailureFileRows,
  matchingParsableRows,
  mergeMatchingParseAndValidationReports,
} from "../utils/buildMatchingParseFailureReport";
import {
  applyBulkMatchAction,
  summariseBulkMatchAction,
} from "../utils/matchingBulkActions";
import {
  addEditedMatchingIndex,
  addEditedMatchingIndices,
} from "../utils/matchingImportClustering";
import { parseMatchingCsv } from "../utils/parseMatchingCsv";
import type { MatchingCsvAction, MatchingRow } from "../types/matching";
import styles from "./MatchingImportComposer.module.css";

export interface MatchingImportComposerProps {
  /** Notifies the layout when company/pay-run/UX-variant steps should lock. */
  onImportLockChange?: (locked: boolean) => void;
}

function useWizard() {
  const ctx = useContext(MatchingWizardCtx);
  if (!ctx) {
    throw new Error(
      "MatchingImportComposer must render inside MatchingWizardProvider"
    );
  }
  return ctx;
}

export function MatchingImportComposer({
  onImportLockChange,
}: MatchingImportComposerProps = {}) {
  const wizard = useWizard();
  const {
    boundPayRun,
    fileName,
    rawCsvText,
    parsedRows,
    parseErrors,
    failedUploadAttempt,
    manager,
    setUploadSuccess,
    setUploadParseFailure,
    updateParsedRows,
    setFailedUploadAttempt,
    clearUpload,
  } = wizard;

  const hasParseErrors = parseErrors.length > 0;

  const [dialogMode, setDialogMode] = useState<MatchingConfirmMode>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [pendingReplace, setPendingReplace] = useState<{
    file: File;
    text: string;
  } | null>(null);
  const [dismissedWarningIds, setDismissedWarningIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  // 0-based row indexes edited since the last validation — mirrors Pay
  // Component Import's `editedRowIndexes`. Reset on new upload/clear and on
  // a successful (non-stale) validate completion; see effect below.
  const [editedIndices, setEditedIndices] = useState<ReadonlySet<number>>(
    new Set()
  );
  const [selectedIndices, setSelectedIndices] = useState<ReadonlySet<number>>(
    new Set()
  );
  const [bulkConfirm, setBulkConfirm] = useState<{
    action: MatchingCsvAction;
    scope: MatchingBulkScope;
    /** Snapshotted at confirm-open so selection churn cannot drift the apply. */
    indices: readonly number[];
  } | null>(null);
  const shouldAutoValidateRef = useRef(false);
  /** Bumped to re-run the auto-validate effect after bulk MatchAction (etc.). */
  const [autoValidateTick, setAutoValidateTick] = useState(0);
  /** True while a bulk/upload auto-validate is queued or in flight (UI gate). */
  const [pendingAutoValidate, setPendingAutoValidate] = useState(false);
  /** Persistent aria-live announcement (bulk bar unmounts after apply). */
  const [bulkAnnounce, setBulkAnnounce] = useState("");
  const fileReadSequenceRef = useRef(0);
  const wasValidatingRef = useRef(false);

  const parseStubFileRows = useMemo(
    () => matchingParseFailureFileRows(parseErrors),
    [parseErrors]
  );

  const queueAutoValidate = useCallback(() => {
    shouldAutoValidateRef.current = true;
    setPendingAutoValidate(true);
    setAutoValidateTick((t) => t + 1);
  }, []);

  // When some rows failed parse, keep the full CSV (incl. stubs) on screen
  // even though the manager only holds the parsable subset for validate/commit.
  const displayRows: MatchingRow[] = useMemo(() => {
    if (hasParseErrors && parsedRows && parsedRows.length > 0) {
      return parsedRows;
    }
    return manager.rows.length > 0 ? manager.rows : (parsedRows ?? []);
  }, [hasParseErrors, manager.rows, parsedRows]);

  const goodRowsForParse = useMemo(
    () =>
      hasParseErrors && parsedRows
        ? matchingParsableRows(parsedRows, parseErrors)
        : [],
    [hasParseErrors, parseErrors, parsedRows]
  );

  const parseFailureReport = useMemo(() => {
    if (!hasParseErrors || displayRows.length === 0) return null;
    return buildMatchingParseFailureReport(displayRows, parseErrors);
  }, [displayRows, hasParseErrors, parseErrors]);

  const activeResult = useMemo(() => {
    const apiResult = manager.commitResult ?? manager.validationReport;
    if (apiResult && hasParseErrors && parsedRows && goodRowsForParse.length > 0) {
      return mergeMatchingParseAndValidationReports(
        parsedRows,
        parseErrors,
        goodRowsForParse,
        apiResult
      );
    }
    if (apiResult) return apiResult;
    return parseFailureReport;
  }, [
    goodRowsForParse,
    hasParseErrors,
    manager.commitResult,
    manager.validationReport,
    parseErrors,
    parseFailureReport,
    parsedRows,
  ]);

  const phase =
    manager.commitResult !== null
      ? "committed"
      : manager.validationReport !== null || hasParseErrors
        ? "validated"
        : "idle";

  const activeWarnings = (activeResult?.warnings ?? []).filter(
    (w) => !dismissedWarningIds.has(`${w.code}:${w.indices.join(",")}`)
  );

  const rowCount = displayRows.length;

  const failedCount = activeResult?.failed ?? 0;
  // Rows withheld from commit: blocking errors + skip-promise warnings
  // (MATCH-ALIGNED / MATCH-NO-OVERRIDE). Used for confirm-modal counts so we
  // do not overstate how many rows will actually write.
  const SKIP_ON_COMMIT_CODES = new Set(["MATCH-ALIGNED", "MATCH-NO-OVERRIDE"]);
  const blockingErrorIndices = new Set(
    (activeResult?.errors ?? [])
      .map((error) => error.index)
      .filter((index): index is number => index !== null)
  );
  const skipWarningIndices = new Set(
    (activeResult?.warnings ?? [])
      .filter((warning) => SKIP_ON_COMMIT_CODES.has(warning.code))
      .flatMap((warning) => warning.indices)
  );
  const skipOnCommitIndices = new Set<number>([
    ...blockingErrorIndices,
    ...skipWarningIndices,
  ]);
  const skippedOnCommitCount = skipOnCommitIndices.size;
  const passedCount = Math.max(rowCount - skippedOnCommitCount, 0);
  // AC-23: all rows are skip-warnings (no blocking errors) — still confirm.
  const isSkipOnlyConfirm =
    blockingErrorIndices.size === 0 &&
    skipWarningIndices.size > 0 &&
    passedCount === 0 &&
    rowCount > 0;
  const blockedRowIndices = skipOnCommitIndices;
  const keptOverrideCount = displayRows.filter(
    (row, index) =>
      row.matchAction === "DELETE_KEEP_AMOUNT" && !blockedRowIndices.has(index)
  ).length;
  const clearedOverrideCount = displayRows.filter(
    (row, index) =>
      row.matchAction === "DELETE" && !blockedRowIndices.has(index)
  ).length;

  // Skip-promise warnings are automatic (not overrideable). Other warnings
  // still use the override confirm path.
  const overrideableWarnings = activeWarnings.filter(
    (w) => !SKIP_ON_COMMIT_CODES.has(w.code)
  );

  // Confirm import stays available even with blocking errors present — those
  // rows are withheld from the commit and stay visible as errors afterwards
  // (partial import, mirrors Work Patterns / Pay Components). Row-level parse
  // failures work the same way: parsable rows validate/commit; stubs stay Fail.
  // Skip-only batches (AC-23) are also confirmable — zero writes, all skipped.
  const commitReady =
    manager.validationReport !== null &&
    !manager.validationStale &&
    (passedCount > 0 || isSkipOnlyConfirm) &&
    manager.commitResult === null &&
    !manager.isValidating &&
    !manager.isCommitting;

  // Keep the report mounted through a re-validate/commit instead of hiding
  // it — the previous (stale) result stays visible underneath the progress
  // panel so the user doesn't lose their place (mirrors Work Patterns, which
  // keeps its table/clusters mounted and only swaps button labels while
  // busy). Row-level parse failures also mount the report (stubs + Fail chips).
  const showReport =
    !!parsedRows &&
    (manager.validationReport !== null ||
      manager.commitResult !== null ||
      hasParseErrors);

  const reviewBusy = manager.isValidating || manager.isCommitting;

  // Leave-page guard (#leave-page-consistency, mirrors Work Patterns /
  // Pay Component Import): block in-app navigation and warn on tab close
  // while there's uncommitted progress, so uploaded rows/edits/validation
  // aren't silently discarded by navigating away.
  // Parse-only batches (every row failed parse) leave manager.rows empty but
  // still have editable stubs on parsedRows — treat that as uncommitted progress.
  const hasUploadProgress =
    manager.hasUncommittedImportProgress ||
    manager.isCommitting ||
    (parsedRows !== null && parsedRows.length > 0) ||
    hasParseErrors;

  const { isBlocked, stay, leaveAnyway } = useUnsavedProgressNavigationGuard(
    hasUploadProgress,
    [COMPONENT_MATCHING_IMPORT_BASE_PATH]
  );
  const handleLeaveAnyway = useCallback(() => {
    clearUpload();
    leaveAnyway();
  }, [clearUpload, leaveAnyway]);

  // Lock company / pay-run / UX-variant steps while there is uncommitted
  // progress or a validate/commit is in flight, so context can't change
  // mid-import (provider remount via key would discard the batch).
  useEffect(() => {
    const locked =
      hasUploadProgress || manager.isValidating || manager.isCommitting;
    onImportLockChange?.(locked);
    return () => onImportLockChange?.(false);
  }, [
    hasUploadProgress,
    manager.isCommitting,
    manager.isValidating,
    onImportLockChange,
  ]);

  // Serialize auto-validate: while a validate is in flight, leave the ref set
  // so this effect re-runs once and issues a single follow-up (no overlap).
  useEffect(() => {
    if (
      !shouldAutoValidateRef.current ||
      !manager.hasRows ||
      manager.isConfigLoading ||
      manager.isValidating
    ) {
      return;
    }
    shouldAutoValidateRef.current = false;
    void manager.validate();
  }, [manager, autoValidateTick]);

  // Clear pending UI gate only when no further auto-validate is queued —
  // otherwise a second bulk apply during validate would flash Re-validate.
  const wasValidatingForPendingRef = useRef(false);
  useEffect(() => {
    if (
      pendingAutoValidate &&
      wasValidatingForPendingRef.current &&
      !manager.isValidating &&
      !shouldAutoValidateRef.current
    ) {
      setPendingAutoValidate(false);
    }
    wasValidatingForPendingRef.current = manager.isValidating;
  }, [manager.isValidating, pendingAutoValidate, autoValidateTick]);

  // Fresh validation clears the "edited since last run" grouping — mirrors
  // Pay Component Import's WizardProvider, which resets `editedRowIndexes`
  // to [] whenever a dry-run/import result lands. `updateRowsLocally` only
  // flips `validationStale` true (it never touches `validationReport`), so
  // this only fires once a validate() call actually completes successfully.
  useEffect(() => {
    if (
      wasValidatingRef.current &&
      !manager.isValidating &&
      !manager.validationStale
    ) {
      setEditedIndices(new Set());
    }
    wasValidatingRef.current = manager.isValidating;
  }, [manager.isValidating, manager.validationStale]);

  const applyFile = useCallback(
    (file: File, text: string) => {
      const outcome = parseMatchingCsv(text, {
        boundPayRunId: boundPayRun?.payRunId ?? null,
      });

      setSelectedFile(file);
      setDismissedWarningIds(new Set());
      setEditedIndices(new Set());
      setSelectedIndices(new Set());
      setEditingRowIndex(null);
      setBulkConfirm(null);
      setBulkAnnounce("");

      if (!outcome.ok) {
        if (outcome.rows && outcome.rows.length > 0) {
          // Row-level failures: clear prior batch, show stubs + parsable rows,
          // and auto-validate the parsable subset for partial import.
          setUploadParseFailure(
            file.name,
            text,
            outcome.rows,
            outcome.errors
          );
          const parsable = matchingParsableRows(outcome.rows, outcome.errors);
          shouldAutoValidateRef.current = parsable.length > 0;
          setPendingAutoValidate(parsable.length > 0);
          return;
        }
        shouldAutoValidateRef.current = false;
        setPendingAutoValidate(false);
        // File-level failure (missing columns, empty, etc.): wipe prior
        // success so only the alert for this attempt remains.
        clearUpload();
        setSelectedFile(file);
        setFailedUploadAttempt({
          fileName: file.name,
          rawCsvText: text,
          parseErrors: outcome.errors,
        });
        return;
      }
      setUploadSuccess(file.name, text, outcome.rows);
      shouldAutoValidateRef.current = true;
      setPendingAutoValidate(true);
    },
    [
      boundPayRun?.payRunId,
      clearUpload,
      setFailedUploadAttempt,
      setUploadParseFailure,
      setUploadSuccess,
    ]
  );

  const readAndMaybeReplace = useCallback(
    (file: File | null) => {
      if (!file) return;
      const sequence = ++fileReadSequenceRef.current;
      const reader = new FileReader();
      reader.onload = () => {
        if (sequence !== fileReadSequenceRef.current) return;
        const text = String(reader.result ?? "");
        if (hasUploadProgress && parsedRows) {
          setPendingReplace({ file, text });
          setDialogMode("replace");
          return;
        }
        applyFile(file, text);
      };
      reader.readAsText(file);
    },
    [applyFile, hasUploadProgress, parsedRows]
  );

  const downloadIssues = useCallback(() => {
    if (!rawCsvText || !activeResult) return;
    const errors = activeResult.errors ?? [];
    const warnings = (activeResult.warnings ?? []).filter(
      (w) => !dismissedWarningIds.has(`${w.code}:${w.indices.join(",")}`)
    );
    const content = buildMatchingIssuesCsv(
      rawCsvText,
      displayRows,
      errors,
      warnings
    );
    downloadMatchingIssuesCsv(
      matchingIssuesFilename(fileName ?? "matching.csv"),
      content
    );
  }, [
    activeResult,
    dismissedWarningIds,
    displayRows,
    fileName,
    rawCsvText,
  ]);

  const downloadParseFailures = useCallback(() => {
    const errors =
      failedUploadAttempt?.parseErrors ??
      (hasParseErrors ? parseErrors : null);
    if (!errors) return;
    const content = buildMatchingParseFailureCsv(errors);
    downloadMatchingIssuesCsv(
      matchingIssuesFilename(
        failedUploadAttempt?.fileName ?? fileName ?? "matching.csv"
      ),
      content
    );
  }, [
    failedUploadAttempt,
    fileName,
    hasParseErrors,
    parseErrors,
  ]);

  // Post-commit "start a new import" — mirrors Work Patterns' onClearAll:
  // clears immediately with no confirmation modal, since there is no
  // uncommitted progress to lose once the batch has committed.
  const startNewImport = useCallback(() => {
    clearUpload();
    setSelectedFile(null);
    setEditedIndices(new Set());
    setSelectedIndices(new Set());
    setEditingRowIndex(null);
    setBulkConfirm(null);
    setBulkAnnounce("");
  }, [clearUpload]);

  const confirmDialog = () => {
    if (dialogMode === "replace" && pendingReplace) {
      applyFile(pendingReplace.file, pendingReplace.text);
      setPendingReplace(null);
      setDialogMode(null);
      return;
    }
    if (dialogMode === "commit" || dialogMode === "override") {
      void manager.commit(true);
      setDialogMode(null);
    }
  };

  const handleCancelAnyway = useCallback(() => {
    setCancelConfirmOpen(false);
    clearUpload();
    setSelectedFile(null);
    setEditedIndices(new Set());
    setSelectedIndices(new Set());
    setEditingRowIndex(null);
    setBulkConfirm(null);
    setBulkAnnounce("");
  }, [clearUpload]);

  // Clear selection once a commit lands (no further bulk edits offered).
  useEffect(() => {
    if (manager.commitResult !== null) {
      setSelectedIndices(new Set());
      setBulkConfirm(null);
      setBulkAnnounce("");
    }
  }, [manager.commitResult]);

  // Edit flow: the Composer owns `editingRowIndex` (mirrors Pay Component
  // Import's ReviewScreen, which owns edit state at the screen level and
  // passes callbacks down into the table). Hidden/disabled once the batch
  // has committed — the correct path after a commit is "Import new matching
  // file", not editing rows that have already been written.
  const handleEditRow = useCallback((index: number) => {
    setEditingRowIndex(index);
  }, []);

  const handleToggleRow = useCallback((index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleSetVisibleSelection = useCallback(
    (indices: readonly number[], selected: boolean) => {
      setSelectedIndices((prev) => {
        const next = new Set(prev);
        for (const index of indices) {
          if (selected) next.add(index);
          else next.delete(index);
        }
        return next;
      });
    },
    []
  );

  const executeBulkApply = useCallback(
    (action: MatchingCsvAction, indices: readonly number[]) => {
      if (indices.length === 0) return;

      const summary = summariseBulkMatchAction(displayRows, indices, action);
      if (summary.changed === 0) {
        setBulkAnnounce(
          `No changes — selected rows already use ${action}.`
        );
        setSelectedIndices(new Set());
        setBulkConfirm(null);
        return;
      }

      const nextRows = applyBulkMatchAction(displayRows, indices, action);
      const rowLabel = summary.changed === 1 ? "row" : "rows";

      if (hasParseErrors && fileName && rawCsvText) {
        setUploadParseFailure(fileName, rawCsvText, nextRows, parseErrors);
        setEditedIndices((prev) => addEditedMatchingIndices(prev, indices));
        const canValidate =
          matchingParsableRows(nextRows, parseErrors).length > 0;
        if (canValidate) {
          queueAutoValidate();
          setBulkAnnounce(
            `Set ${summary.changed} ${rowLabel} to ${action}. Re-validating.`
          );
        } else {
          setBulkAnnounce(
            `Set ${summary.changed} ${rowLabel} to ${action}.`
          );
        }
      } else {
        updateParsedRows(nextRows);
        setEditedIndices((prev) => addEditedMatchingIndices(prev, indices));
        // Same auto-validate path as a fresh upload — bulk MatchAction is a
        // batch rewrite, so re-run validation without an extra click.
        if (nextRows.length > 0) {
          queueAutoValidate();
          setBulkAnnounce(
            `Set ${summary.changed} ${rowLabel} to ${action}. Re-validating.`
          );
        } else {
          setBulkAnnounce(`Set ${summary.changed} ${rowLabel} to ${action}.`);
        }
      }
      setSelectedIndices(new Set());
      setBulkConfirm(null);
    },
    [
      displayRows,
      fileName,
      hasParseErrors,
      parseErrors,
      queueAutoValidate,
      rawCsvText,
      setUploadParseFailure,
      updateParsedRows,
    ]
  );

  const handleBulkApplyRequest = useCallback(
    (action: MatchingCsvAction, _scope: MatchingBulkScope) => {
      const indices = [...selectedIndices];
      if (indices.length === 0) return;

      const summary = summariseBulkMatchAction(displayRows, indices, action);
      if (summary.changed === 0) {
        setBulkAnnounce(
          `No changes — selected rows already use ${action}.`
        );
        setSelectedIndices(new Set());
        return;
      }

      // Whole-file when every selectable (non-parse-stub) row is selected.
      const selectableCount = displayRows.filter(
        (row) => !parseStubFileRows.has(row.fileRow)
      ).length;
      const scope: MatchingBulkScope =
        selectableCount > 0 && indices.length === selectableCount
          ? "all"
          : "selection";

      // Confirm destructive verbs, amount clears (REAPPLY / DELETE*), and
      // whole-file APPLY. One-click APPLY/REAPPLY on a partial selection.
      const needsConfirm =
        scope === "all" ||
        action === "DELETE" ||
        action === "DELETE_KEEP_AMOUNT" ||
        summary.amountCleared > 0;
      if (needsConfirm) {
        setBulkConfirm({ action, scope, indices });
        return;
      }
      executeBulkApply(action, indices);
    },
    [
      displayRows,
      executeBulkApply,
      parseStubFileRows,
      selectedIndices,
    ]
  );

  const bulkConfirmSummary = useMemo(() => {
    if (!bulkConfirm) return null;
    return summariseBulkMatchAction(
      displayRows,
      bulkConfirm.indices,
      bulkConfirm.action
    );
  }, [bulkConfirm, displayRows]);

  const handleRowSave = useCallback(
    (updatedRow: MatchingRow) => {
      if (editingRowIndex === null) return;
      const nextRows = displayRows.map((row, i) =>
        i === editingRowIndex ? updatedRow : row
      );

      // Parse-failure batch: manager only holds the parsable subset. Drop the
      // parse error for the edited file row (fixed in the modal), refresh
      // parsedRows + pending good rows, and re-validate.
      if (hasParseErrors && fileName && rawCsvText) {
        const nextParseErrors = parseErrors.filter(
          (e) => e.fileRow !== updatedRow.fileRow
        );
        setUploadParseFailure(
          fileName,
          rawCsvText,
          nextRows,
          nextParseErrors
        );
        if (matchingParsableRows(nextRows, nextParseErrors).length > 0) {
          queueAutoValidate();
        }
        setEditedIndices(new Set());
        setEditingRowIndex(null);
        return;
      }

      updateParsedRows(nextRows);
      setEditedIndices((prev) => addEditedMatchingIndex(prev, editingRowIndex));
      setEditingRowIndex(null);
    },
    [
      displayRows,
      editingRowIndex,
      fileName,
      hasParseErrors,
      parseErrors,
      queueAutoValidate,
      rawCsvText,
      setUploadParseFailure,
      updateParsedRows,
    ]
  );

  const editingRow =
    editingRowIndex !== null ? displayRows[editingRowIndex] ?? null : null;
  const editingRowErrors =
    editingRowIndex !== null
      ? (activeResult?.errors ?? [])
          .filter((e) => e.index === editingRowIndex)
          .map((e) => e.error)
      : [];

  return (
    <div className={styles.composer}>
      <header className={styles.header}>
        <H2>Matching file</H2>
        {boundPayRun && (
          <Body className={styles.help}>
            {boundPayRun.payGroupName} · period ending {boundPayRun.periodEnd} ·{" "}
            {boundPayRun.payRunId}
          </Body>
        )}
      </header>

      <FileDropzone
        value={selectedFile}
        compactWhenSelected={!!parsedRows || !!failedUploadAttempt}
        onChange={(files) => {
          const file = Array.isArray(files) ? (files[0] ?? null) : files;
          readAndMaybeReplace(file);
        }}
        instruction="Drop your matching CSV here, or click to browse"
        subInstruction="CSV with MatchAction (APPLY, REAPPLY, DELETE, DELETE_KEEP_AMOUNT), EmployeeCode, ComponentName, and TargetAmount. StartDate and FinishDate are optional."
        accept=".csv"
        multiple={false}
        isDisabled={manager.isValidating || manager.isCommitting}
        buttonText="Select CSV file"
        fileFilter={(file) => file.name.toLowerCase().endsWith(".csv")}
      />

      {parsedRows && (
        <p className={styles.fileStatus} role="status">
          {rowCount} matching row{rowCount === 1 ? "" : "s"} parsed
          {hasParseErrors
            ? ` · ${parseErrors.length} parse error${
                parseErrors.length === 1 ? "" : "s"
              }`
            : ""}
        </p>
      )}

      {failedUploadAttempt && (
        <div className={styles.parseErrors} role="alert">
          <Body>
            Could not use {failedUploadAttempt.fileName}. Fix these issues and
            try again:
          </Body>
          <ul>
            {failedUploadAttempt.parseErrors.map((e, i) => (
              <li key={`${e.fileRow}-${i}`}>
                {e.fileRow ? `Row ${e.fileRow}: ` : ""}
                {e.message}
              </li>
            ))}
          </ul>
          <div className={styles.parseErrorActions}>
            <Button variant="secondary" onPress={downloadParseFailures}>
              Download issues CSV
            </Button>
          </div>
        </div>
      )}

      {manager.managerError && (
        <Body className={styles.error} role="alert">
          {manager.managerError}
        </Body>
      )}

      {(manager.isValidating || manager.isCommitting) && (
        <ImportProgress
          heading={
            manager.isValidating
              ? "Validating matches…"
              : "Importing matches…"
          }
          progressbarLabel={
            manager.isValidating
              ? "Validation in progress"
              : "Import in progress"
          }
          detail={
            manager.isValidating
              ? `Validating ${rowCount} matching row${
                  rowCount === 1 ? "" : "s"
                }…`
              : `Importing ${rowCount} matching row${
                  rowCount === 1 ? "" : "s"
                }…`
          }
        />
      )}

      {showReport && activeResult && (
        <MatchingImportReport
          rows={displayRows}
          result={activeResult}
          phase={phase}
          validationStale={!hasParseErrors && manager.validationStale}
          dismissedWarningIds={dismissedWarningIds}
          onDismissWarning={(id) =>
            setDismissedWarningIds((prev) => new Set([...prev, id]))
          }
          editedIndices={editedIndices}
          onEditRow={phase === "committed" ? undefined : handleEditRow}
          editDisabled={reviewBusy}
          dismissDisabled={reviewBusy}
          parseFailure={hasParseErrors}
          parseFailureFileRows={
            hasParseErrors ? parseStubFileRows : undefined
          }
          selectedIndices={
            phase === "committed" ? undefined : selectedIndices
          }
          onToggleRow={phase === "committed" ? undefined : handleToggleRow}
          onSetVisibleSelection={
            phase === "committed" ? undefined : handleSetVisibleSelection
          }
          selectionDisabled={reviewBusy}
        />
      )}

      {(parsedRows || failedUploadAttempt) && (
        <div className={styles.stickyFooterStack}>
          <p className={styles.bulkAnnounce} role="status" aria-live="polite">
            {bulkAnnounce}
          </p>
          {showReport &&
            activeResult &&
            phase !== "committed" &&
            selectedIndices.size > 0 && (
              <MatchingBulkActionBar
                selectedCount={selectedIndices.size}
                totalCount={displayRows.length}
                isDisabled={reviewBusy}
                onApply={handleBulkApplyRequest}
                onClearSelection={() => setSelectedIndices(new Set())}
              />
            )}
          <WorkflowActionBar
          sticky={false}
          ariaLabel="Component matching import actions"
          status={
            manager.commitResult ? (
              <span
                className={
                  manager.commitResult.failed > 0
                    ? styles.warn
                    : styles.success
                }
              >
                {`Import results: ${manager.commitResult.succeeded} of ${manager.commitResult.total} succeeded.`}
              </span>
            ) : failedUploadAttempt ||
              (hasParseErrors && goodRowsForParse.length === 0) ? (
              <span className={styles.warn}>
                Fix the file and upload again before validating.
              </span>
            ) : manager.validationStale &&
              manager.hasRows &&
              !manager.isValidating &&
              !manager.isCommitting &&
              !pendingAutoValidate ? (
              <span className={styles.stale}>
                Rows changed since the last validation. Re-validate before
                importing.
              </span>
            ) : undefined
          }
          leadingActions={
            phase === "committed" ? (
              <Button
                variant="secondary"
                onPress={startNewImport}
                isDisabled={manager.isValidating || manager.isCommitting}
              >
                Import new matching file
              </Button>
            ) : (
              <Button
                variant="secondary"
                onPress={() => setCancelConfirmOpen(true)}
                isDisabled={manager.isValidating || manager.isCommitting}
              >
                Cancel import
              </Button>
            )
          }
          trailingActions={
            <>
              {(failedCount > 0 || activeWarnings.length > 0) &&
                !manager.isValidating &&
                !manager.isCommitting && (
                  <Button variant="secondary" onPress={downloadIssues}>
                    Download issues CSV
                  </Button>
                )}
              {manager.validationStale &&
                manager.hasRows &&
                !manager.isValidating &&
                !manager.isCommitting &&
                !pendingAutoValidate && (
                  <Button
                    variant="primary"
                    onPress={() => void manager.validate()}
                    isDisabled={manager.isValidating || manager.isCommitting}
                  >
                    Re-validate
                  </Button>
                )}
              {commitReady && overrideableWarnings.length === 0 && (
                <Button
                  variant="primary"
                  onPress={() => setDialogMode("commit")}
                >
                  {isSkipOnlyConfirm
                    ? `Confirm (${rowCount} skipped)`
                    : skippedOnCommitCount > 0
                      ? `Confirm import (${passedCount} of ${rowCount})`
                      : "Confirm import"}
                </Button>
              )}
              {commitReady && overrideableWarnings.length > 0 && (
                <Button
                  variant="primary"
                  onPress={() => setDialogMode("override")}
                >
                  Override warnings and import
                </Button>
              )}
            </>
          }
        />
        </div>
      )}

      <MatchingImportConfirmModal
        mode={dialogMode}
        rowCount={rowCount}
        skippedCount={skippedOnCommitCount}
        warningCount={overrideableWarnings.length}
        keptOverrideCount={keptOverrideCount}
        clearedOverrideCount={clearedOverrideCount}
        onConfirm={confirmDialog}
        onCancel={() => {
          setPendingReplace(null);
          setDialogMode(null);
        }}
      />

      <MatchingImportCancelConfirmModal
        isOpen={cancelConfirmOpen}
        onKeepImporting={() => setCancelConfirmOpen(false)}
        onCancelAnyway={handleCancelAnyway}
      />

      <MatchingRowEditModal
        isOpen={editingRowIndex !== null}
        row={editingRow}
        rowErrors={editingRowErrors}
        isProcessing={reviewBusy}
        onCancel={() => setEditingRowIndex(null)}
        onSave={handleRowSave}
      />

      <MatchingBulkActionConfirmModal
        isOpen={bulkConfirm !== null}
        action={bulkConfirm?.action ?? null}
        scope={bulkConfirm?.scope ?? null}
        summary={bulkConfirmSummary}
        onConfirm={() => {
          if (!bulkConfirm) return;
          executeBulkApply(bulkConfirm.action, bulkConfirm.indices);
        }}
        onCancel={() => setBulkConfirm(null)}
      />

      <UnsavedProgressConfirmModal
        isOpen={isBlocked}
        title="Leave component matching import?"
        message="Uploaded rows, edits, and validation progress will be lost."
        onStay={stay}
        onLeave={handleLeaveAnyway}
      />
    </div>
  );
}
